import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDistrictsWithAssignedDetails } from "@/store/slices/masterSlice";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toggleDistrictStatus } from "@/store/slices/masterSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";

const DistrictManagement = () => {
  const dispatch = useDispatch();
  const { districts, loading } = useSelector((state) => state.master);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10); // items per page

  useEffect(() => {
    dispatch(fetchDistrictsWithAssignedDetails());
  }, [dispatch]);

  const [selectedDistrict, setSelectedSDistrict] = useState(null);
  // Pagination logic
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentDistricts = districts.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(districts.length / perPage);

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const openApproveModal = (district) => {
    setSelectedSDistrict(district);
    setApproveModalOpen(true);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const getStatusBadge = (status) => {
    const st = status || "Active";
    let variant = "default";

    switch (st) {
      case "Active":
        variant = "success"; // green
        break;
      case "Inactive":
        variant = "secondary"; // gray
        break;
      case "Pending":
        variant = "warning"; // yellow
        break;
      case "Rejected":
        variant = "destructive"; // red
        break;
      default:
        variant = "default";
    }

    return <Badge variant={variant}>{st}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">District Management</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Districts</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sl.No.</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentDistricts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center">
                        No districts found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentDistricts.map((district, index) => (
                      <TableRow key={district.id}>
                        <TableCell>{indexOfFirst + index + 1}</TableCell>
                        <TableCell>{district.name}</TableCell>
                        <TableCell>{district.code}</TableCell>
                        <TableCell>{district.state}</TableCell>
                        <TableCell>
                          {district.assigned_to || "Not Assigned"}
                        </TableCell>
                        <TableCell>
                          <div
                            className="cursor-pointer"
                            onClick={() => openApproveModal(district)}
                          >
                            <Badge
                              variant={
                                district.status === "Active"
                                  ? "success"
                                  : "secondary"
                              }
                              className="cursor-pointer"
                            >
                              {district.status || "Inactive"}
                            </Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex justify-end space-x-2 mt-4">
                <Button onClick={handlePrev} disabled={currentPage === 1}>
                  Previous
                </Button>
                <span className="flex items-center px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to change the status of this district?
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setApproveModalOpen(false);
                setSelectedSDistrict(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedDistrict) {
                  dispatch(toggleDistrictStatus(selectedDistrict.id))
                    .unwrap()
                    .then(() => {
                      toast({
                        title: "",
                        description: "District status updated successfully!",
                        variant: "success",
                      });
                      setApproveModalOpen(false); // Close the dialog
                      setSelectedSDistrict(null); // Reset selected district
                    })
                    .catch((err) => {
                      toast.error(
                        err.message || "Failed to update district status."
                      );
                    });
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistrictManagement;
